import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import "./SuccessPage.css";
import ThreeStepIndicator from "../components/ThreeStepIndicator";
import "../components/ThreeStepIndicator.css";
import { roundPriceStep } from "../utils/roundPriceStep";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import InlinePayOptions from "../components/InlinePayOptions";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost";
const stripePromise = STRIPE_PK && isSecure ? loadStripe(STRIPE_PK) : null;

type OrderStatus = "pending" | "accepted" | "queued" | "played" | "declined";
type ProgressStep = 0 | 1 | 2;

interface OrderInfo {
  orderId: string;
  status: OrderStatus;
  searchQuery: string;
  amount?: number | null;
  currency?: string;
  trackImage?: string | null;
  trackArtists?: string[];
  trackAlbum?: string;
  isPriority?: boolean;
  tier?: "standard" | "priority" | "fast" | null;
  declineReason?: "pas_ambience" | "no_access" | "more_money";
  counterAmount?: number | null;
  djSlug?: string;
}

const POLLING_INTERVAL_MS = 5000;

const normCur = (cur?: string) => String(cur || "").trim().toLowerCase();
const curSym = (cur?: string) => {
  switch (normCur(cur)) {
    case "usd":
      return "$";
    case "eur":
      return "€";
    case "gbp":
      return "£";
    case "rub":
      return "₽";
    default:
      return "$";
  }
};
const round2 = (n: number) => Math.round(n * 100) / 100;

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionIdRaw = searchParams.get("session_id");
  const orderIdRaw = searchParams.get("order_id");

  const sessionId =
    sessionIdRaw && /^cs_[A-Za-z0-9_]+$/.test(sessionIdRaw) ? sessionIdRaw : null;
  const orderIdParam =
    orderIdRaw && /^[a-f0-9]{24}$/i.test(orderIdRaw) ? orderIdRaw : null;

  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const slug =
    searchParams.get("slug") || sessionStorage.getItem("lastDjSlug") || "";
  const [hasBeenAccepted, setHasBeenAccepted] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const [showPayInline, setShowPayInline] = useState(false);
  const [piClientSecret, setPiClientSecret] = useState<string | null>(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [emailForLink, setEmailForLink] = useState("");
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const inlinePayRef = useRef<HTMLDivElement | null>(null);

  const [inlineMode, setInlineMode] = useState<null | "priority" | "counter">(null);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (!showConfetti) return;
    const t = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(t);
  }, [showConfetti]);

  const getCompletedStep = (status: OrderStatus): ProgressStep => {
    if (status === "pending" || status === "declined") return 1;
    if (status === "accepted" || status === "queued" || status === "played") return 2;
    return 0;
  };

  useEffect(() => {
    const run = async () => {
      try {
        if (sessionId) {
          const res = await api.post<OrderInfo>("/payments/confirm", { sessionId });
          setOrder(res.data);
          if (res.data?.djSlug) sessionStorage.setItem("lastDjSlug", res.data.djSlug);
          if (["accepted", "queued", "played"].includes(res.data.status)) setHasBeenAccepted(true);
          setShowConfetti(true);
          setLoading(false);
          return;
        }

        if (orderIdParam) {
          try {
            const res = await api.post<OrderInfo>("/payments/confirm-intent", { orderId: orderIdParam });
            setOrder(res.data);
            if (res.data?.djSlug) sessionStorage.setItem("lastDjSlug", res.data.djSlug);
            if (["accepted", "queued", "played"].includes(res.data.status)) setHasBeenAccepted(true);
            setShowConfetti(true);
            setLoading(false);
            return;
          } catch {
            try {
              const res2 = await api.get<OrderInfo>(`/orders/order-status/${orderIdParam}`);
              setOrder(res2.data);
              if (res2.data?.djSlug) sessionStorage.setItem("lastDjSlug", res2.data.djSlug);
              if (["accepted", "queued", "played"].includes(res2.data.status)) setHasBeenAccepted(true);
              setShowConfetti(true);
              setLoading(false);
              return;
            } catch (e2: any) {
              setErrorMsg(e2?.response?.data?.msg || "Error loading order after payment");
              setLoading(false);
              return;
            }
          }
        }

        setErrorMsg("Missing session_id or order_id in URL");
        setLoading(false);
      } catch (err: any) {
        console.error("Success confirm error:", err);
        setErrorMsg(err?.response?.data?.msg || "Error while confirming payment");
        setLoading(false);
      }
    };

    run();
  }, [sessionId, orderIdParam, searchParams]);

  useEffect(() => {
    if (!order) return;
    if (["accepted", "queued", "played", "declined"].includes(order.status)) return;

    const intervalId = setInterval(() => {
      api
        .get<OrderInfo>(`/orders/order-status/${order.orderId}`)
        .then((res) => {
          if (!res?.data) return;
          const incoming = res.data as OrderInfo;
          const merged: OrderInfo = {
            ...incoming,
            isPriority:
              typeof incoming.isPriority === "boolean"
                ? incoming.isPriority
                : order.isPriority,
            tier:
              typeof incoming.tier !== "undefined"
                ? incoming.tier
                : (order as any).tier,
          };
          if (!order || merged.status !== order.status) {
            setOrder(merged);
            if (["accepted", "queued", "played"].includes(merged.status)) {
              setHasBeenAccepted(true);
            }
          }
        })
        .catch((err) => console.error("Polling error:", err));
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [order]);

  const handlePriorityClick = async () => {
    if (!order) return;
    if (!stripePromise) {
      setErrorMsg("Stripe publishable key is missing on the frontend");
      return;
    }
    try {
      const { data } = await api.post<{ clientSecret: string; orderId: string }>(
        "/payments/create-priority-intent",
        { orderId: order.orderId }
      );

      setPiClientSecret(data.clientSecret);
      setShowPayInline(true);
      setInlineMode("priority");
      setShowCardForm(false);
      setPayError(null);

      setTimeout(() => {
        inlinePayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      console.error("Priority intent error:", err);
      setErrorMsg("Failed to start priority payment");
    }
  };

  const handleAcceptCounter = async () => {
    if (!order) return;
    if (!stripePromise) {
      setErrorMsg("Stripe publishable key is missing on the frontend");
      return;
    }
    try {
      const { data } = await api.post<{ clientSecret: string; orderId: string }>(
        "/payments/create-counter-intent",
        { orderId: order.orderId }
      );

      setPiClientSecret(data.clientSecret);
      setShowPayInline(true);
      setInlineMode("counter");
      setShowCardForm(false);
      setPayError(null);

      setTimeout(() => {
        inlinePayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      console.error("Counter intent error:", err);
      setErrorMsg("Failed to start counter payment");
    }
  };

  const handlePrioritySuccess = async () => {
    if (!order) return;
    try {
      const res = await api.post<OrderInfo>("/payments/confirm-intent", {
        orderId: order.orderId,
      });
      setOrder(res.data);
      setShowPayInline(false);
      setShowCardForm(false);
      setPayError(null);
      setInlineMode(null);
      setPiClientSecret(null);
    } catch (e) {
      console.error("confirm after priority/counter error:", e);
    }
  };

  const handleUserDecline = () => {
    if (slug) window.location.href = `/dj/${slug}/request`;
    else window.history.back();
  };

  if (loading) {
    return (
      <div className="success-container">
        <p className="status-text">Finishing your request…</p>
      </div>
    );
  }
  if (errorMsg) {
    return (
      <div className="success-container">
        <p className="status-text error">{errorMsg}</p>
      </div>
    );
  }
  if (!order) return null;

  const {
    searchQuery,
    amount,
    currency,
    trackImage,
    trackArtists = [],
    status,
    isPriority,
    declineReason,
    counterAmount,
    djSlug,
  } = order;

  const sym = curSym(currency);

  const normTier = (t: any) =>
    typeof t === "string"
      ? ((t as string).toLowerCase() as "standard" | "priority" | "fast")
      : t;

  const baseRaw = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  const tierVal = normTier((order as any).tier);
  const isUpgrade = !!isPriority && (!tierVal || tierVal === "standard");
  const baseShown = isUpgrade ? roundPriceStep(baseRaw * 2) : roundPriceStep(baseRaw);

  const total = baseShown;
  const feeDisplay = Math.max(0, +(total - baseShown).toFixed(2));
  const counterBase = Number.isFinite(Number(counterAmount)) ? Number(counterAmount) : 0;
  const counterTotal = roundPriceStep(counterBase);
  const counterFeeDisplay = Math.max(0, +(counterTotal - counterBase).toFixed(2));

  const completedStep = getCompletedStep(status);

  const getDeclineReasonLabel = (
    r?: "pas_ambience" | "no_access" | "more_money"
  ): string | null => {
    if (!r) return null;
    switch (r) {
      case "pas_ambience":
        return "Not the Vibe";
      case "no_access":
        return "The artist doesn’t have access to this song";
      case "more_money":
        return null;
      default:
        return null;
    }
  };

  const targetSlug = slug || djSlug || "";
  const hasCounter =
    declineReason === "more_money" &&
    typeof counterAmount === "number" &&
    counterAmount > 0;
  const reasonLabel = getDeclineReasonLabel(declineReason);
  const isCounter = status === "declined" && hasCounter;

  const isAcceptedLike =
    hasBeenAccepted || ["accepted", "queued", "played"].includes(status);

  const shouldShowPriorityButton =
    !isPriority && !isCounter && status !== "declined" && !isAcceptedLike && baseRaw > 0;

  return (
    <div className="success-container">
      {showConfetti && (
        <div className="confetti-container" aria-hidden>
          {Array.from({ length: 40 }).map((_, i) => (
            <div
              key={i}
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
                backgroundColor: ["#22c55e", "#f59e0b", "#3b82f6", "#ef4444", "#a855f7", "#ec4899"][i % 6],
              }}
            />
          ))}
        </div>
      )}
      <h1 className="success-heading">Track Request</h1>

      {isCounter ? (
        <div className="card counter-card">
          <div className="track-image-wrapper">
            {trackImage ? (
              <img src={trackImage} alt={searchQuery} className="track-image" />
            ) : (
              <div className="track-image-placeholder" />
            )}
          </div>

          <div className="track-info">
            <h2 className="track-title">{searchQuery}</h2>
            {trackArtists.length > 0 && (
              <p className="track-artists">{trackArtists.join(", ")}</p>
            )}
          </div>

          <div className="counter-title">
            <span className="counter-emoji" aria-hidden>
              🎵🎵
            </span>
            Artist Raised the Stakes
          </div>

          {reasonLabel && <div className="counter-reason">“{reasonLabel}”</div>}

          <div className="counter-price-line">
            <span className="price-value">
              {sym}{counterTotal.toFixed(2)}
            </span>
          </div>

          <div className="sp-total-box">
            <div className="sp-total-title">
              Requested for {sym}{counterTotal.toFixed(2)}
            </div>
          </div>

          <div className="button-group">
            <button
              className="btn-accept-raise"
              onClick={handleAcceptCounter}
            >
              💸 Yes, Play It ({sym}{counterTotal.toFixed(2)})
            </button>

            <div ref={inlinePayRef} style={{ width: "100%" }}>
              {showPayInline && stripePromise && piClientSecret && inlineMode === "counter" && (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: piClientSecret,
                    appearance: { theme: "night" },
                    locale: "en",
                  }}
                >
                  <InlinePayOptions
                    orderId={order.orderId}
                    emailForLink={emailForLink}
                    setEmailForLink={setEmailForLink}
                    showCardForm={showCardForm}
                    setShowCardForm={setShowCardForm}
                    processing={processing}
                    setProcessing={setProcessing}
                    payError={payError}
                    setPayError={setPayError}
                    selectedTrack={null}
                    totalToCharge={counterTotal}
                    onPaymentSuccess={handlePrioritySuccess}
                  />
                </Elements>
              )}
            </div>

            <button className="btn-decline-raise" onClick={handleUserDecline}>
              🙅‍♂️ Nah, I’ll Pass
            </button>

            <button
              className="btn-make-another"
              onClick={() => {
                if (slug) window.location.href = `/dj/${slug}/request?prev_order_id=${order.orderId}`;
                else window.history.back();
              }}
            >
              Request Another Song
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="track-image-wrapper">
            {trackImage ? (
              <img src={trackImage} alt={searchQuery} className="track-image" />
            ) : (
              <div className="track-image-placeholder" />
            )}
          </div>

          <div className="track-info">
            <h2 className="track-title">{searchQuery}</h2>
            {trackArtists.length > 0 && (
              <p className="track-artists">{trackArtists.join(", ")}</p>
            )}
            {baseShown > 0 && (
              <p className="track-artist">
                Requested for {sym}{total.toFixed(2)}
              </p>
            )}
          </div>

          <div className="sp-total-box">
            <div className="sp-total-title">
              Requested for {sym}{total.toFixed(2)}
            </div>
          </div>
          <div className={normTier((order as any).tier) === "fast" ? "is-fast" : ""}>
            <ThreeStepIndicator status={status} isPriority={normTier((order as any).tier) === "fast"} />
          </div>

          <div className="button-group">
            {shouldShowPriorityButton && (
              <button
                className="btn-priority btn-priority--rocket"
                onClick={handlePriorityClick}
              >
                <span className="ft">
                  <span className="ft-txt">
                    <span className="ft-line1">Fast Pass <span className="rq-bolt-emoji-succes">⚡</span></span>
                    <span className="ft-line2">(Double Your Bid)</span>
                  </span>
                </span>
              </button>
            )}

            <div ref={inlinePayRef} style={{ width: "100%" }}>
              {showPayInline && stripePromise && piClientSecret && inlineMode === "priority" && (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: piClientSecret,
                    appearance: { theme: "night" },
                    locale: "en",
                  }}
                >
                  <InlinePayOptions
                    orderId={order.orderId}
                    emailForLink={emailForLink}
                    setEmailForLink={setEmailForLink}
                    showCardForm={showCardForm}
                    setShowCardForm={setShowCardForm}
                    processing={processing}
                    setProcessing={setProcessing}
                    payError={payError}
                    setPayError={setPayError}
                    selectedTrack={null}
                    totalToCharge={total}
                    onPaymentSuccess={handlePrioritySuccess}
                  />
                </Elements>
              )}
            </div>

            <button
              className="btn-make-another"
              onClick={() => {
                if (slug) window.location.href = `/dj/${slug}/request?prev_order_id=${order.orderId}`;
                else window.history.back();
              }}
            >
              Request Another Song
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuccessPage;
